import {TextArea} from '@dropbox/dig-components/text_fields';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useQueries} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import cx from 'classnames';
import type {
  JiraObject,
  Workstream,
  WorkstreamBasic,
  WorkstreamBasicWithSummary,
  WorkstreamDependency,
  WorkstreamMetric,
} from 'client';
import {
  EditorWorkstreamType,
  WorkstreamDependencyType,
  WorkstreamEdit,
  WorkstreamService,
} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {useCollabId, usePlanningId} from 'components/DSYS/CollabProvider';
import {markdownBracketReference} from 'components/DSYS/editor/dynamicNode/markdownExport';
import {
  getEditorStateMapEntry,
  mergeEditorStateMapJsonWithSource,
  WORKSTREAM_TABLE_EDITOR_STATE_MAP_KEY,
} from 'components/DSYS/editor/dynamicNode/overlayHelpers';
import {PlanningCommentField} from 'components/DSYS/editor/dynamicNode/PlanningCommentField';
import {planningMapKeys} from 'components/DSYS/editor/dynamicNode/planningCommentMapKeys';
import {PlanningStructuredCommentField} from 'components/DSYS/editor/dynamicNode/PlanningStructuredCommentField';
import {attachPreservedColumnWidths} from 'components/DSYS/editor/dynamicNode/planningTable/columnWidthPersistence';
import {getTableWidthPx} from 'components/DSYS/editor/dynamicNode/planningTable/getTableWidthPx';
import {useColumnResizePersistence} from 'components/DSYS/editor/dynamicNode/planningTable/useColumnResizePersistence';
import {
  PLANNING_TABLE_SHELL_STYLE,
  PlanningTableWideShell,
} from 'components/DSYS/editor/dynamicNode/planningTable/WideTableShell';
import {structuredCommentSegment} from 'components/DSYS/editor/dynamicNode/structuredCommentState';
import type {
  DynamicNodeDefinition,
  DynamicNodeRenderProps,
} from 'components/DSYS/editor/dynamicNode/types';
import {
  type MergePersistedMapKeyArgs,
  type PersistEditorStateMapKey,
} from 'components/DSYS/editor/dynamicNode/useDynamicNodeEditorStateMap';
import {
  getRootWorkstreamIdFromValue,
  resolveSwitchboardTableEmbedRootId,
} from 'components/DSYS/editor/dynamicNode/workstreamEmbedRootResolution';
import {$isDynamicNode} from 'components/DSYS/editor/nodes/DynamicNode';
import {getTypeaheadNodeTableContext} from 'components/DSYS/editor/nodes/TypeaheadNode';
import {
  AddWorkstreamComponent,
  WorkstreamDriWrapper,
  WorkstreamNameWrapper,
  WorkstreamsOverflowMenu,
} from 'components/DSYS/editor/nodes/WorkstreamEditComponent';
import {TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND} from 'components/DSYS/editor/plugins/SelectedTextToolbar';
import type {EditorTextSize} from 'components/DSYS/editor/themes/EditorTheme';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {PersonHoverCard} from 'components/DSYS/HoverCard';
import {Link} from 'components/DSYS/Link';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import type {Column} from 'components/DSYS/Table';
import {Table} from 'components/DSYS/Table';
import {DependencyStatusScores} from 'components/workstreams/DependencyStatusScores';
import {CURRENT_YEAR, ROUTE_PATHS} from 'constant';
import {isUserAdmin} from 'helpers/utils';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {$getRoot, $isElementNode, type LexicalEditor, type LexicalNode} from 'lexical';
import {
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {getService} from 'utilities';
import {
  useEditorWorkstreams,
  useIsPlanningDocPublished,
  usePlanning,
  useWorkstreamsUpdate,
} from 'views/planning/hooks';
import {queryClient} from 'views/QueryClientWrapper';
import {useWorkstreamDetail, workstreamDataKeys} from 'views/workstreams/detail/hooks';
import {SubworkstreamSectionHeaderRow} from 'views/workstreams/detail/SubworkstreamSectionHeaderRow';
import {COLUMN_CONFIGS} from 'views/workstreams/detail/types';
import {
  buildMetricsPeriodRange,
  compareMetricsPeriods,
  getCurrentMetricsPeriod,
  getMetricsPeriodWithOffset,
  type MetricsForecastType,
  normalizeMetricsPeriodOptions,
  type PlanningCollabOutcomeCommentsConfig,
  REDESIGN_OUTCOME_FILTERS,
  RedesignMetricsList,
  RedesignMetricsPeriodSelector,
  type RedesignOutcomeFilter,
  RedesignOutcomesList,
  RedesignOutcomeTypeSelector,
} from 'views/workstreams/detail/WorkstreamRedesignSections';
import workstreamTableStyles from 'views/workstreams/detail/WorkstreamTable.module.css';
import {
  useWorkstream,
  useWorkstreamEdit,
  useWorkstreamMetricMutation,
} from 'views/workstreams/hooks';
import {useWorkstreamRoadmapInitiatives} from 'views/workstreams/jiraObjects';
import {
  getFullWorkstreamMetrics,
  getWorkstreamDri,
  isFullWorkstreamMetric,
  isWorkstreamStatusArchived,
} from 'views/workstreams/utils';
import {WorkstreamRedesignSubworkstreamSummaryRow} from 'views/workstreams/WorkstreamNameRedesignShared';

import {
  getWorkstreamTableDescriptionState,
  toRichTextValueForEmbed,
} from './workstreamTableDescription';
import embedStyles from './WorkstreamTableEmbed.module.css';

/** Toggle persisted workstream embed column widths (Lexical `value.columnWidths`). */
const ENABLE_SYNC_COLUMNS = true;
export const WORKSTREAM_TABLE_COMMENT_ORIGIN_ID_KEY = 'commentOriginId' as const;
const PLANNING_TRACKER_DEFAULT_METRICS_FORECAST_TYPE: MetricsForecastType = 'budget';
const getPlanningTrackerDefaultMetricsPeriod = () => `${CURRENT_YEAR}EOY`;

type WorkstreamTableEmbedMetricMutations = ReturnType<typeof useWorkstreamMetricMutation>;

export const WORKSTREAM_TABLE_EMBED_KIND = 'workstream_table_embed';

export type WorkstreamTableEmbedBinding = {
  kind: typeof WORKSTREAM_TABLE_EMBED_KIND;
};

const getRecordValue = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

export const getWorkstreamTableCommentOriginIdFromValue = (value: unknown): string | undefined => {
  const raw = getRecordValue(value)?.[WORKSTREAM_TABLE_COMMENT_ORIGIN_ID_KEY];
  return typeof raw === 'string' && raw ? raw : undefined;
};

const createWorkstreamTableCommentOriginId = (): string => {
  const randomId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `workstream-table:${randomId}`;
};

const attachWorkstreamTableCommentOriginId = (value: unknown, originId: string): unknown => {
  if (typeof value === 'number') {
    return {v: value, [WORKSTREAM_TABLE_COMMENT_ORIGIN_ID_KEY]: originId};
  }

  const recordValue = getRecordValue(value);
  if (recordValue) {
    return {...recordValue, [WORKSTREAM_TABLE_COMMENT_ORIGIN_ID_KEY]: originId};
  }

  return {[WORKSTREAM_TABLE_COMMENT_ORIGIN_ID_KEY]: originId};
};

const attachPreservedWorkstreamTableMetadata = (
  nextValue: unknown,
  previousValue: unknown
): unknown => {
  let next = ENABLE_SYNC_COLUMNS
    ? attachPreservedColumnWidths(nextValue, previousValue)
    : nextValue;
  const originId = getWorkstreamTableCommentOriginIdFromValue(previousValue);
  if (originId) {
    next = attachWorkstreamTableCommentOriginId(next, originId);
  }
  return next;
};

export const valueWithWorkstreamTableCommentOriginId = ({
  value,
  rootId,
  originId,
}: {
  value: unknown;
  rootId: number | undefined;
  originId: string;
}): unknown => {
  if (typeof value === 'number' || getRecordValue(value)) {
    return attachWorkstreamTableCommentOriginId(value, originId);
  }

  if (typeof rootId === 'number' && rootId > 0) {
    return attachWorkstreamTableCommentOriginId(rootId, originId);
  }

  return attachWorkstreamTableCommentOriginId({}, originId);
};

/** Detail (and some summary) payloads may include `dependencies`; the OpenAPI summary type does not. */
type WorkstreamWithOptionalDeps = WorkstreamBasicWithSummary & {
  dependencies?: WorkstreamDependency[];
  hidden_mnpi_metrics_count?: number | null;
};

/**
 * The edit modal deliberately keeps confidential metrics hidden in every rollout state.
 * Combine the server's viewer-specific aggregate with redacted rows from newer payloads.
 */
export const getEmbedHiddenMnpiMetricsCount = (workstream: WorkstreamWithOptionalDeps): number => {
  const redactedMetricCount = workstream.metrics.filter(
    (metric) => !isFullWorkstreamMetric(metric)
  ).length;

  return Math.max(workstream.hidden_mnpi_metrics_count ?? 0, redactedMetricCount);
};

const getSubworkstreamCount = (workstream: WorkstreamBasicWithSummary): number => {
  const w = workstream as WorkstreamWithOptionalDeps;
  if (w.dependencies?.length !== undefined) {
    return w.dependencies.length;
  }
  return w.dependencies_count ?? 0;
};

const sortDependenciesByStatusAndOrder = (a: WorkstreamDependency, b: WorkstreamDependency) => {
  const statusPriority = (status?: string) => {
    if (status === 'archived') return 3;
    if (status === 'cancelled' || status === 'missed') return 2;
    if (status === 'complete') return 1;
    return 0;
  };
  const aP = statusPriority(a.dependent.updates?.[0]?.status);
  const bP = statusPriority(b.dependent.updates?.[0]?.status);
  if (aP !== bP) return aP - bP;
  return a.order - b.order;
};

const EmbedNameColumnMeta = ({
  workstream,
  isDependency,
  isSubworkstreamsExpanded,
  onToggleSubworkstreams,
  suppressSubworkstreamUi,
  driEditor,
  workstreamDriPlanningComments,
  driClassName,
  driInputClassName,
  driTextSize = 'small',
}: {
  workstream: WorkstreamBasicWithSummary;
  isDependency: boolean;
  isSubworkstreamsExpanded: boolean;
  onToggleSubworkstreams?: () => void;
  suppressSubworkstreamUi?: boolean;
  /** When set (narrative planning embed), DRI uses the same EmployeeSelect flow as typeahead DRI cells. */
  driEditor?: LexicalEditor;
  /** When set and `driEditor` is unset, DRI name is commentable in the planning collab doc. */
  workstreamDriPlanningComments?: {
    planningId: string | undefined;
    mapJson: string | undefined;
    onPersistKey: PersistEditorStateMapKey;
  };
  driClassName?: string;
  driInputClassName?: string;
  driTextSize?: 'small' | 'medium';
}) => {
  const dri = getWorkstreamDri(workstream);
  const subworkstreamCount = getSubworkstreamCount(workstream);
  const hasSubworkstreams = subworkstreamCount > 0;
  const canExpandSubworkstreams = hasSubworkstreams && !isDependency;
  const dependenciesForScores = (workstream as WorkstreamWithOptionalDeps).dependencies;

  return (
    <Stack gap="4">
      {canExpandSubworkstreams && !suppressSubworkstreamUi ? (
        <WorkstreamRedesignSubworkstreamSummaryRow
          subworkstreamCount={subworkstreamCount}
          isExpanded={isSubworkstreamsExpanded}
          onToggle={onToggleSubworkstreams}
          statusScores={
            dependenciesForScores?.length ? (
              <DependencyStatusScores dependencies={dependenciesForScores} />
            ) : undefined
          }
        />
      ) : null}
      {hasSubworkstreams && isDependency && !suppressSubworkstreamUi ? (
        <WorkstreamRedesignSubworkstreamSummaryRow
          subworkstreamCount={subworkstreamCount}
          isDependency
          statusScores={
            dependenciesForScores?.length ? (
              <DependencyStatusScores dependencies={dependenciesForScores} />
            ) : undefined
          }
        />
      ) : null}
      {driEditor ? (
        <WorkstreamDriWrapper
          editor={driEditor}
          workstreamId={workstream.id}
          showFullEmployeeName
          containerClassName={driClassName}
          inputClassName={driInputClassName}
        />
      ) : dri ? (
        <Box display="flex" alignItems="center" className={driClassName} style={{gap: 4}}>
          <Avatar user={dri} size="xsmall" />
          {workstreamDriPlanningComments ? (
            <PlanningCommentField
              mapKey={planningMapKeys.workstreamTable.dri(workstream.id)}
              mapJson={workstreamDriPlanningComments.mapJson}
              fallbackText={dri.name}
              planningId={workstreamDriPlanningComments.planningId}
              onPersistKey={workstreamDriPlanningComments.onPersistKey}
              textColor="subtle"
              textSize={driTextSize}
              fallback={(text) => (
                <PersonHoverCard ldap={dri.ldap} name={dri.name} role={dri.role}>
                  <Link to={`${ROUTE_PATHS.PROFILE.replace(':userid', dri.ldap)}`} hasNoUnderline>
                    <Text size={driTextSize}>{text}</Text>
                  </Link>
                </PersonHoverCard>
              )}
            />
          ) : (
            <PersonHoverCard ldap={dri.ldap} name={dri.name} role={dri.role}>
              <Link to={`${ROUTE_PATHS.PROFILE.replace(':userid', dri.ldap)}`} hasNoUnderline>
                <Text size={driTextSize}>{dri.name}</Text>
              </Link>
            </PersonHoverCard>
          )}
        </Box>
      ) : null}
    </Stack>
  );
};

function getEditorStateMapJsonFromValue(value: unknown): string | undefined {
  if (value && typeof value === 'object' && WORKSTREAM_TABLE_EDITOR_STATE_MAP_KEY in value) {
    const raw = (value as Record<string, unknown>)[WORKSTREAM_TABLE_EDITOR_STATE_MAP_KEY];
    if (typeof raw === 'string') {
      return raw;
    }
  }
  return undefined;
}

function getCollabTitleForEntity(value: unknown, entityId: number): string | undefined {
  const raw = getEditorStateMapJsonFromValue(value);
  return getEditorStateMapEntry(raw, planningMapKeys.workstreamTable.title(entityId));
}

const EmbedWorkstreamTitleObjectiveBlock = ({
  title,
  description,
  overflowAction,
}: {
  title: string;
  description: string | null | undefined;
  overflowAction?: ReactNode;
}) => {
  const descriptionValue = toRichTextValueForEmbed(description);
  const showDescription = !!description;
  const hasInlineAction = Boolean(overflowAction);

  return (
    <>
      <Box
        className={workstreamTableStyles.workstreamTitleWrapper}
        display={hasInlineAction ? 'flex' : undefined}
        alignItems={hasInlineAction ? 'flex-start' : undefined}
        width={hasInlineAction ? '100%' : undefined}
        style={hasInlineAction ? {gap: 6, minWidth: 0} : {position: 'relative'}}
      >
        <Box
          className={workstreamTableStyles.workstreamTitleInner}
          flexGrow={hasInlineAction ? 1 : undefined}
          style={hasInlineAction ? {minWidth: 0} : undefined}
        >
          <Text
            isBold
            className={cx(
              workstreamTableStyles.workstreamTitleText,
              workstreamTableStyles.workstreamTitleTextRedesign
            )}
          >
            <span
              className={cx(!hasInlineAction && workstreamTableStyles.workstreamTitleLinkRedesign)}
            >
              <Truncate lines={2} tooltipControlProps={{placement: 'bottom'}}>
                {title}
              </Truncate>
            </span>
          </Text>
        </Box>
        {overflowAction ? (
          <Box flexShrink={0} display="inline-flex" alignItems="center">
            {overflowAction}
          </Box>
        ) : null}
      </Box>
      {showDescription ? (
        <Box className={workstreamTableStyles.descriptionBlock}>
          <Stack gap="8">
            <RichTextArea
              editable={false}
              autoFocus={false}
              value={descriptionValue}
              squareBorders
              textColor="subtle"
              textSize="small"
            />
          </Stack>
        </Box>
      ) : null}
    </>
  );
};

const EmbedPlanningTitleObjectiveBlock = ({
  workstreamId,
  title,
  description,
  mapJson,
  planningId,
  onPersistMapKey,
  overflowAction,
  titleTextSize = 'medium',
}: {
  workstreamId: number;
  title: string;
  description: string | null | undefined;
  mapJson: string | undefined;
  planningId: string | undefined;
  onPersistMapKey: PersistEditorStateMapKey;
  overflowAction?: ReactNode;
  titleTextSize?: EditorTextSize;
}) => {
  const titleObjectiveMapKey = planningMapKeys.workstreamTable.titleAndDescription(workstreamId);
  const descriptionState = getWorkstreamTableDescriptionState({description});
  const hasInlineAction = Boolean(overflowAction);
  const objectiveText = descriptionState.showDescription
    ? parseAndExtractTextFromLexicalJson(descriptionState.fallbackText)
    : '';
  const titleObjectiveLines = [
    [structuredCommentSegment('title', title)],
    ...(objectiveText ? [[structuredCommentSegment('description', objectiveText)]] : []),
  ];

  const titleContentClassName = cx(
    workstreamTableStyles.workstreamTitleText,
    workstreamTableStyles.workstreamTitleTextRedesign,
    !hasInlineAction && workstreamTableStyles.workstreamTitleLinkRedesign,
    embedStyles.embedWorkstreamTitleObjectiveContent
  );

  return (
    <>
      <Box
        className={workstreamTableStyles.workstreamTitleWrapper}
        display={hasInlineAction ? 'flex' : undefined}
        alignItems={hasInlineAction ? 'flex-start' : undefined}
        width={hasInlineAction ? '100%' : undefined}
        style={hasInlineAction ? {gap: 6, minWidth: 0} : {position: 'relative'}}
      >
        <Box
          className={workstreamTableStyles.workstreamTitleInner}
          flexGrow={hasInlineAction ? 1 : undefined}
          minWidth={hasInlineAction ? '0' : undefined}
        >
          <PlanningStructuredCommentField
            planningId={planningId}
            mapKey={titleObjectiveMapKey}
            mapJson={mapJson}
            lines={titleObjectiveLines}
            editable={false}
            hideToolbar
            disableBorder
            minHeight={0}
            editablePaddingX="0"
            editablePaddingY="0"
            squareBorders
            textSize={titleTextSize}
            contentClassName={titleContentClassName}
            onPersistKey={onPersistMapKey}
          />
        </Box>
        {overflowAction ? (
          <Box flexShrink={0} display="inline-flex" alignItems="center">
            {overflowAction}
          </Box>
        ) : null}
      </Box>
    </>
  );
};

const NarrativePlanningEmbedDescriptionBlock = ({
  workstream,
  planningId,
  persistMapKey,
}: {
  workstream: WorkstreamBasicWithSummary;
  planningId: string | undefined;
  persistMapKey: PersistEditorStateMapKey;
}) => {
  const descMapKey = planningMapKeys.workstreamTable.description(workstream.id);
  const descriptionState = getWorkstreamTableDescriptionState({
    description: workstream.description,
  });

  if (!descriptionState.showDescription) {
    return null;
  }

  return (
    <Box className={workstreamTableStyles.descriptionBlock}>
      <Stack gap="8">
        <PlanningCommentField
          mapKey={descMapKey}
          mapJson={undefined}
          fallbackText={descriptionState.fallbackText}
          planningId={planningId}
          onPersistKey={persistMapKey}
          textColor="subtle"
          textSize="small"
        />
      </Stack>
    </Box>
  );
};

const EmbedNameColumn = ({
  workstream,
  isDependency,
  isSubworkstreamsExpanded,
  onToggleSubworkstreams,
  suppressSubworkstreamUi,
}: {
  workstream: WorkstreamBasicWithSummary;
  isDependency: boolean;
  isSubworkstreamsExpanded: boolean;
  onToggleSubworkstreams?: () => void;
  suppressSubworkstreamUi?: boolean;
}) => {
  return (
    <Box paddingLeft={isDependency ? '20' : undefined}>
      <Stack gap="4">
        <EmbedWorkstreamTitleObjectiveBlock
          title={workstream.name}
          description={workstream.description}
        />
        <EmbedNameColumnMeta
          workstream={workstream}
          isDependency={isDependency}
          isSubworkstreamsExpanded={isSubworkstreamsExpanded}
          onToggleSubworkstreams={onToggleSubworkstreams}
          suppressSubworkstreamUi={suppressSubworkstreamUi}
        />
      </Stack>
    </Box>
  );
};

const NarrativePlanningEmbedNameColumn = ({
  editor,
  workstream,
  isDependency,
  isSubworkstreamsExpanded,
  onToggleSubworkstreams,
  suppressSubworkstreamUi,
  embedValue,
  planningId,
  onCollabTitleChange,
  persistMapKey,
  onRemoveFromPlanning,
}: {
  editor: LexicalEditor;
  workstream: WorkstreamBasicWithSummary;
  isDependency: boolean;
  isSubworkstreamsExpanded: boolean;
  onToggleSubworkstreams?: () => void;
  suppressSubworkstreamUi?: boolean;
  embedValue: unknown;
  planningId: string | undefined;
  onCollabTitleChange: (
    entityId: number,
    contentEditorState: string | undefined,
    sourceText?: string
  ) => void;
  persistMapKey: PersistEditorStateMapKey;
  onRemoveFromPlanning: (workstreamId: number) => Promise<void>;
}) => {
  return (
    <Box paddingLeft={isDependency ? '20' : undefined}>
      <Stack gap="4">
        <WorkstreamNameWrapper
          editor={editor}
          workstreamId={workstream.id}
          onRemoveWorkstream={onRemoveFromPlanning}
          initialContentEditorState={getCollabTitleForEntity(embedValue, workstream.id)}
          handleContentEditorStateChange={onCollabTitleChange}
          hideDescription
          nameTextSize="medium"
        />
        <NarrativePlanningEmbedDescriptionBlock
          workstream={workstream}
          planningId={planningId}
          persistMapKey={persistMapKey}
        />
        <EmbedNameColumnMeta
          workstream={workstream}
          isDependency={isDependency}
          isSubworkstreamsExpanded={isSubworkstreamsExpanded}
          onToggleSubworkstreams={onToggleSubworkstreams}
          suppressSubworkstreamUi={suppressSubworkstreamUi}
          driEditor={editor}
          driClassName={isDependency ? embedStyles.embedSubworkstreamDri : undefined}
          driInputClassName={isDependency ? embedStyles.embedSubworkstreamDriInput : undefined}
        />
      </Stack>
    </Box>
  );
};

const toWorkstreamEditPayload = (ws: Workstream): WorkstreamEdit =>
  ({
    ...ws,

    metrics: ws.metrics.map((metric) => ({
      ...metric,
      owner_id: metric.owner?.user_id ?? null,
      url: metric.url ?? null,
      is_mnpi: metric.is_mnpi ?? false,
    })),

    dependencies: ws.dependencies.map((dependency) => ({
      ...dependency,
      dependent_id: dependency.id,
    })),

    employee_associations: ws.employee_associations.map((assoc) => ({
      dri: assoc.dri,
      user_id: assoc.employee.user_id,
    })),

    private_employees: ws.private_employees?.map(({user_id}) => user_id),
    private_teams: ws.private_teams?.map(({team_id}) => team_id),
  }) as WorkstreamEdit;

const stopSubworkstreamNameShortcutPropagation = (event: KeyboardEvent<HTMLTextAreaElement>) => {
  if (!event.metaKey && !event.ctrlKey) {
    return;
  }

  const key = event.key.toLowerCase();
  if (key === 'a' || key === 'c' || key === 'x') {
    event.stopPropagation();
  }
};

const stopSubworkstreamNameClipboardPropagation = (event: ClipboardEvent<HTMLTextAreaElement>) => {
  event.stopPropagation();
};

const SwitchboardPlanningEmbedNameColumn = ({
  editor,
  workstream,
  isDependency,
  parentSwitchboardId,
  isSubworkstreamsExpanded,
  onToggleSubworkstreams,
  suppressSubworkstreamUi,
  embedValue,
  planningId,
  persistEditorStateMapKey,
}: {
  editor: LexicalEditor;
  workstream: WorkstreamBasicWithSummary;
  isDependency: boolean;
  parentSwitchboardId: number;
  isSubworkstreamsExpanded: boolean;
  onToggleSubworkstreams?: () => void;
  suppressSubworkstreamUi?: boolean;
  embedValue: unknown;
  planningId: string | undefined;
  persistEditorStateMapKey: PersistEditorStateMapKey;
}) => {
  const {workstream: parentWorkstream} = useWorkstream(parentSwitchboardId);
  const {workstream: fullWorkstream} = useWorkstream(workstream.id);
  // Lazy load full workstream data to keep workstream up to date whenever it changes
  const workstreamToUse = fullWorkstream ?? workstream;
  const {editWorkstream} = useWorkstreamEdit();

  const isWorkstreamRestricted = workstreamToUse.name === 'Restricted';

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(workstreamToUse.name);

  useEffect(() => {
    setNameDraft(workstreamToUse.name);
  }, [workstreamToUse.name]);

  useEffect(() => {
    if (isWorkstreamRestricted) {
      setIsEditingName(false);
    }
  }, [isWorkstreamRestricted]);

  const handleRemoveSubworkstream = useCallback(async () => {
    if (!parentWorkstream || !isDependency) {
      return;
    }
    await editWorkstream({
      reason: 'Delete subworkstream',
      data: {
        ...toWorkstreamEditPayload(parentWorkstream),
        dependencies: parentWorkstream.dependencies
          .filter((dependency) => dependency.dependent.id !== workstreamToUse.id)
          .map((dependency) => ({
            ...dependency,
            dependent_id: dependency.id,
          })),
      } as WorkstreamEdit,
    });
  }, [editWorkstream, isDependency, parentWorkstream, workstreamToUse.id]);

  const saveWorkstreamName = useCallback(async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setNameDraft(workstreamToUse.name);
      return;
    }
    if (!fullWorkstream || isWorkstreamRestricted) {
      return;
    }
    if (trimmed === fullWorkstream.name) {
      setNameDraft(fullWorkstream.name);
      return;
    }
    editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: false});
    await editWorkstream({
      reason: 'Update workstream name',
      data: {...toWorkstreamEditPayload(fullWorkstream), name: trimmed},
    });
    editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
  }, [
    editWorkstream,
    editor,
    fullWorkstream,
    isWorkstreamRestricted,
    nameDraft,
    workstreamToUse.name,
  ]);

  const startEditingWorkstreamName = useCallback(() => {
    setNameDraft(workstreamToUse.name);
    setIsEditingName(true);
  }, [workstreamToUse.name]);

  const workstreamOverflowMenu = (
    <WorkstreamsOverflowMenu
      editor={editor}
      workstreamId={workstreamToUse.id}
      onDelete={isDependency ? handleRemoveSubworkstream : undefined}
      setIsEditing={isWorkstreamRestricted ? undefined : startEditingWorkstreamName}
      showDeleteSubworkstreamModal={isDependency}
      showOpenLinkButton
    />
  );

  const mapJson = getEditorStateMapJsonFromValue(embedValue);

  const titleBlock = (() => {
    if (isEditingName && !isWorkstreamRestricted) {
      return (
        <Stack gap="4">
          <TextArea
            name="embed-switchboard-workstream-name"
            value={nameDraft}
            autoFocus
            onFocus={(e) => {
              const end = e.currentTarget.value.length;
              e.currentTarget.setSelectionRange(end, end);
            }}
            onChange={(e) => setNameDraft(e.currentTarget.value)}
            onKeyDownCapture={stopSubworkstreamNameShortcutPropagation}
            onCut={stopSubworkstreamNameClipboardPropagation}
            onCopy={stopSubworkstreamNameClipboardPropagation}
            onBlur={() => {
              void saveWorkstreamName();
              setIsEditingName(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.currentTarget as HTMLTextAreaElement).blur();
              }
            }}
          />
          <NarrativePlanningEmbedDescriptionBlock
            workstream={workstreamToUse}
            planningId={planningId}
            persistMapKey={persistEditorStateMapKey}
          />
        </Stack>
      );
    }

    return (
      <EmbedPlanningTitleObjectiveBlock
        workstreamId={workstreamToUse.id}
        title={workstreamToUse.name}
        description={workstreamToUse.description}
        mapJson={mapJson}
        planningId={planningId}
        onPersistMapKey={persistEditorStateMapKey}
        overflowAction={workstreamOverflowMenu}
        titleTextSize="medium"
      />
    );
  })();

  return (
    <Box paddingLeft={isDependency ? '20' : undefined}>
      <Stack gap="4">
        <Box className={embedStyles.embedSwitchboardNameTitle}>
          <Box flexGrow={1} minWidth="0" className={workstreamTableStyles.workstreamTitleInner}>
            {titleBlock}
          </Box>
        </Box>
        <EmbedNameColumnMeta
          workstream={workstreamToUse}
          isDependency={isDependency}
          isSubworkstreamsExpanded={isSubworkstreamsExpanded}
          onToggleSubworkstreams={onToggleSubworkstreams}
          suppressSubworkstreamUi={suppressSubworkstreamUi}
          driEditor={isDependency ? editor : undefined}
          driClassName={isDependency ? embedStyles.embedSubworkstreamDri : undefined}
          driInputClassName={isDependency ? embedStyles.embedSubworkstreamDriInput : undefined}
          workstreamDriPlanningComments={{
            planningId,
            mapJson,
            onPersistKey: persistEditorStateMapKey,
          }}
        />
      </Stack>
    </Box>
  );
};

type EmbedWorkstreamRow = {
  workstream: WorkstreamBasicWithSummary;
  isDependency: boolean;
};

type EmbedBodyItem =
  | {kind: 'row'; workstream: WorkstreamBasicWithSummary; isDependency: boolean}
  | {kind: 'section_header'; dependencyCount: number};

/** Default column widths (px) — starting grid size; users can widen via DSYS Table resize handles. */
const EMBED_COL_WIDTH_WORKSTREAM_PX = 239;

const EMBED_TABLE_COLUMNS: Column<EmbedWorkstreamRow>[] = [
  {
    type: 'workstream',
    minWidth: 239,
    width: EMBED_COL_WIDTH_WORKSTREAM_PX,
    cantDisable: true,
  },
  {
    type: 'metrics',
    minWidth: 510,
    width: 510,
  },
  {
    type: 'outcomes',
    minWidth: 405,
    width: 405,
    spanner: true,
  },
];

const EMBED_TABLE_LOADING_ROW_COUNT = 5;

const SwitchboardEmbedAddWorkstreamRow = ({
  editor,
  parentSwitchboardId,
  colSpan,
}: {
  editor: LexicalEditor;
  parentSwitchboardId: number;
  colSpan: number;
}) => {
  const {workstream: parentWorkstream} = useWorkstream(parentSwitchboardId);
  const {editWorkstream} = useWorkstreamEdit();

  if (!parentWorkstream) {
    return null;
  }

  const handleAddSubworkstream = async (subworkstream: WorkstreamBasic) => {
    const dependencyIds = parentWorkstream.dependencies.map(
      (dependency) => dependency.dependent.id
    );
    if (subworkstream.id === parentWorkstream.id || dependencyIds.includes(subworkstream.id)) {
      return;
    }

    const dependencies = [...dependencyIds, subworkstream.id];

    await editWorkstream({
      reason: 'Add subworkstream',
      data: {
        ...parentWorkstream,
        metrics: parentWorkstream.metrics.map((metric) => ({
          ...metric,
          owner_id: metric.owner?.user_id ?? null,
          url: metric.url ?? null,
          is_mnpi: metric.is_mnpi ?? false,
        })),
        dependencies: dependencies
          .filter((dependentId) => dependentId > 0)
          .map((dependentId, index) => ({
            dependent_id: dependentId,
            type: WorkstreamDependencyType.DEPENDS_ON,
            order: index + 1,
          })),
        employee_associations: parentWorkstream.employee_associations.map((assoc) => ({
          dri: assoc.dri,
          user_id: assoc.employee.user_id,
        })),
        teams: parentWorkstream.teams?.map(({team_id}) => team_id),
        private_employees: parentWorkstream.private_employees?.map(({user_id}) => user_id),
        private_teams: parentWorkstream.private_teams?.map(({team_id}) => team_id),
      } as WorkstreamEdit,
    });

    const parentId = parentWorkstream.id;
    await queryClient.invalidateQueries({queryKey: ['workstream', parentId], exact: false});
    await queryClient.invalidateQueries({
      queryKey: [...workstreamDataKeys.all, 'workstream-detail', parentId],
      refetchType: 'all',
    });
  };

  const archiveWorkstreams = !!parentWorkstream.archived;
  const driOverride = getWorkstreamDri(parentWorkstream);

  return (
    <Table.Row>
      <Table.Cell colSpan={colSpan}>
        <AddWorkstreamComponent
          editor={editor}
          onSelect={handleAddSubworkstream}
          archiveWorkstreams={archiveWorkstreams}
          driOverride={driOverride}
          buttonLabelKey="add_sub_workstream"
          selectedWorkstreamIds={parentWorkstream.dependencies.map(
            (dependency) => dependency.dependent.id
          )}
          blockedWorkstreamIds={[parentWorkstream.id]}
        />
      </Table.Cell>
    </Table.Row>
  );
};

const WorkstreamTableEmbedTableLoading = ({editor}: {editor: LexicalEditor}) => (
  <PlanningTableWideShell
    editor={editor}
    fillsContainer
    tableWidthPx={getTableWidthPx(EMBED_TABLE_COLUMNS)}
  >
    <Table
      columns={EMBED_TABLE_COLUMNS}
      data={[]}
      bodyCellPadding={8}
      style={PLANNING_TABLE_SHELL_STYLE}
    >
      <Table.Header />
      <Table.Body>
        <Table.Loading rowCount={EMBED_TABLE_LOADING_ROW_COUNT} />
      </Table.Body>
    </Table>
  </PlanningTableWideShell>
);

const EmbedMetricsColumn = ({
  workstream,
  planningCollabOutcomeComments,
  isPlanningDocPublished,
  metricMutations,
  metricPeriodOptions,
  selectedMetricsPeriod,
  selectedMetricsForecastType,
}: {
  workstream: WorkstreamWithOptionalDeps;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  isPlanningDocPublished: boolean;
  metricMutations: WorkstreamTableEmbedMetricMutations;
  metricPeriodOptions: string[];
  selectedMetricsPeriod: string;
  selectedMetricsForecastType: MetricsForecastType;
}) => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const {createMetric, updateMetric, deleteMetric, attachMetric, isPending} = metricMutations;

  const visibleMetrics = useMemo(() => workstream.metrics ?? [], [workstream.metrics]);

  const redesignMetrics = useMemo(
    () => visibleMetrics.filter((m) => m.type === 'metric'),
    [visibleMetrics]
  );

  const owner = getWorkstreamDri(workstream);
  const isWorkstreamRestricted = workstream.name === 'Restricted';
  const disableArchivedMetricsEdit =
    isWorkstreamStatusArchived(workstream as WorkstreamBasic) && !isUserAdmin(pulseUser);

  const persistMetricChange = useCallback(
    async (
      metricid: number,
      metricUpdate: undefined | Partial<WorkstreamMetric>,
      reason?: string,
      skipAuditLogOrExistingMetric?: boolean | WorkstreamMetric
    ) => {
      if (!metricUpdate) return;
      const skipAuditLog =
        typeof skipAuditLogOrExistingMetric === 'boolean' ? skipAuditLogOrExistingMetric : false;
      const existingMetricOverride =
        typeof skipAuditLogOrExistingMetric === 'object' ? skipAuditLogOrExistingMetric : undefined;
      const metricOrder =
        getFullWorkstreamMetrics(workstream.metrics).filter((metric) => metric.type === 'metric')
          .length + 1;
      if (metricid === -1) {
        await createMetric(
          workstream.id,
          metricUpdate,
          metricOrder,
          reason ?? undefined,
          skipAuditLog
        );
      } else {
        const existing =
          existingMetricOverride ??
          getFullWorkstreamMetrics(workstream.metrics).find((metric) => metric.id === metricid);
        if (!existing) return;
        await updateMetric(
          workstream.id,
          metricid,
          existing,
          metricUpdate,
          reason ?? undefined,
          skipAuditLog
        );
      }
    },
    [createMetric, updateMetric, workstream.id, workstream.metrics]
  );

  const handleDeleteMetric = useCallback(
    async (metricToDelete: WorkstreamMetric, reason?: string) => {
      await deleteMetric(
        {workstreamId: workstream.id, metricId: metricToDelete.id!},
        reason ?? undefined
      );
    },
    [deleteMetric, workstream.id]
  );

  const handleAttachMetric = useCallback(
    async (
      attachRequest: {metric_id: number; order?: number | null; is_primary?: boolean},
      reason?: string
    ) => attachMetric(workstream.id, attachRequest, reason ?? undefined),
    [attachMetric, workstream.id]
  );

  const allEditable = useMemo(
    () => visibleMetrics.filter(isFullWorkstreamMetric),
    [visibleMetrics]
  );
  const hiddenMnpiMetricsCount = getEmbedHiddenMnpiMetricsCount(workstream);

  return (
    <RedesignMetricsList
      workstreamId={workstream.id}
      workstreamName={workstream.name}
      metrics={redesignMetrics}
      allEditableMetrics={allEditable}
      owner={owner}
      selectedPeriod={selectedMetricsPeriod}
      selectedForecastType={selectedMetricsForecastType}
      variant="table"
      metricPeriodOptions={metricPeriodOptions}
      commentMentions={[]}
      handleMetricChange={async (metricId, metricUpdate, reason, skipAuditLogOrExistingMetric) => {
        await persistMetricChange(metricId, metricUpdate, reason, skipAuditLogOrExistingMetric);
      }}
      onAttachMetric={handleAttachMetric}
      onDelete={async (metricToDelete, reason) => {
        await handleDeleteMetric(metricToDelete, reason);
      }}
      disableCreateMetric={
        isPlanningDocPublished || isWorkstreamRestricted || disableArchivedMetricsEdit
      }
      isPending={isPending}
      planningCollabOutcomeComments={planningCollabOutcomeComments}
      combineMetricTitleAndMetaComments
      showAllMetricsByDefault
      showNorthStarMetricIcon
      showBaselineColumn
      hiddenMnpiMetricsCount={hiddenMnpiMetricsCount}
    />
  );
};

const EmbedOutcomesColumn = ({
  workstream,
  planningCollabOutcomeComments,
  isPlanningDocPublished,
  metricMutations,
  selectedFilters,
}: {
  workstream: WorkstreamBasicWithSummary;
  planningCollabOutcomeComments?: PlanningCollabOutcomeCommentsConfig;
  isPlanningDocPublished: boolean;
  metricMutations: WorkstreamTableEmbedMetricMutations;
  selectedFilters: RedesignOutcomeFilter[];
}) => {
  const {createMetric, updateMetric, deleteMetric, attachMetric, isPending} = metricMutations;

  const visibleMetrics = useMemo(() => workstream.metrics ?? [], [workstream.metrics]);

  const redesignOutcomeMetrics = useMemo(
    () => visibleMetrics.filter(isFullWorkstreamMetric).filter((m) => m.type !== 'metric'),
    [visibleMetrics]
  );

  const {data: jiraObjects = []} = useWorkstreamRoadmapInitiatives(workstream.id, true);

  const redesignOutcomes = useMemo(
    (): Array<WorkstreamMetric | JiraObject> => [...redesignOutcomeMetrics, ...jiraObjects],
    [redesignOutcomeMetrics, jiraObjects]
  );

  const owner = getWorkstreamDri(workstream);
  const isWorkstreamRestricted = workstream.name === 'Restricted';

  const persistOutcomeMetricChange = useCallback(
    async (
      metricid: number,
      metricUpdate: undefined | Partial<WorkstreamMetric>,
      reason?: string,
      skipAuditLogOrExistingMetric?: boolean | WorkstreamMetric
    ) => {
      if (!metricUpdate) return;
      const skipAuditLog =
        typeof skipAuditLogOrExistingMetric === 'boolean' ? skipAuditLogOrExistingMetric : false;
      const existingMetricOverride =
        typeof skipAuditLogOrExistingMetric === 'object' ? skipAuditLogOrExistingMetric : undefined;
      const order = getFullWorkstreamMetrics(workstream.metrics).length + 1;
      if (metricid === -1) {
        await createMetric(workstream.id, metricUpdate, order, reason ?? undefined, skipAuditLog);
      } else {
        const existing =
          existingMetricOverride ??
          getFullWorkstreamMetrics(workstream.metrics).find((metric) => metric.id === metricid);
        if (!existing) return;
        await updateMetric(
          workstream.id,
          metricid,
          existing,
          metricUpdate,
          reason ?? undefined,
          skipAuditLog
        );
      }
    },
    [createMetric, updateMetric, workstream.id, workstream.metrics]
  );

  const handleDeleteMetric = useCallback(
    async (metricToDelete: WorkstreamMetric, reason?: string) => {
      await deleteMetric(
        {workstreamId: workstream.id, metricId: metricToDelete.id!},
        reason ?? undefined
      );
    },
    [deleteMetric, workstream.id]
  );

  const handleAttachMetric = useCallback(
    async (
      attachRequest: {metric_id: number; order?: number | null; is_primary?: boolean},
      reason?: string
    ) => attachMetric(workstream.id, attachRequest, reason ?? undefined),
    [attachMetric, workstream.id]
  );

  const allEditable = useMemo(
    () => visibleMetrics.filter(isFullWorkstreamMetric),
    [visibleMetrics]
  );

  return (
    <RedesignOutcomesList
      workstreamId={workstream.id}
      workstreamName={workstream.name}
      outcomes={redesignOutcomes}
      allEditableMetrics={allEditable}
      owner={owner}
      selectedFilters={selectedFilters}
      variant="table"
      outcomeTooltipPlacement="bottom-start"
      handleMetricChange={async (metricId, metricUpdate, reason, skipAuditLogOrExistingMetric) => {
        await persistOutcomeMetricChange(
          metricId,
          metricUpdate,
          reason,
          skipAuditLogOrExistingMetric
        );
      }}
      onAttachMetric={handleAttachMetric}
      commentMentions={[]}
      onDelete={async (metricToDelete, reason) => {
        await handleDeleteMetric(metricToDelete, reason);
      }}
      disableCreateMetric={isPlanningDocPublished || isWorkstreamRestricted}
      isPending={isPending}
      planningCollabOutcomeComments={planningCollabOutcomeComments}
      combineOutcomeTitleAndMetaComments
    />
  );
};

function mergePersistedWorkstreamTableEmbedNodeValue(
  {rawNodeValue, mapKey, serializedState, sourceText}: MergePersistedMapKeyArgs,
  isNarrativeFlatMode: boolean
): unknown {
  const cur = rawNodeValue;
  let editorStateMapJson: string | undefined;
  let rootIdFromNode: number | undefined;

  if (typeof cur === 'number') {
    rootIdFromNode = cur > 0 ? cur : undefined;
  } else if (cur && typeof cur === 'object' && !Array.isArray(cur)) {
    const o = cur as {v?: number} & Record<string, unknown>;
    if (typeof o.v === 'number' && o.v > 0) {
      rootIdFromNode = o.v;
    }
    const mapRaw = o[WORKSTREAM_TABLE_EDITOR_STATE_MAP_KEY];
    if (typeof mapRaw === 'string' && mapRaw) {
      editorStateMapJson = mapRaw;
    }
  }

  const mergedEditorStateMapJson = mergeEditorStateMapJsonWithSource(
    editorStateMapJson,
    mapKey,
    serializedState,
    sourceText
  );
  const hasEditorStateMap = mergedEditorStateMapJson !== undefined;

  let nextValue: unknown;
  if (isNarrativeFlatMode) {
    nextValue = hasEditorStateMap
      ? {[WORKSTREAM_TABLE_EDITOR_STATE_MAP_KEY]: mergedEditorStateMapJson}
      : {};
  } else if (rootIdFromNode === undefined) {
    nextValue = undefined;
  } else {
    nextValue = hasEditorStateMap
      ? {
          v: rootIdFromNode,
          [WORKSTREAM_TABLE_EDITOR_STATE_MAP_KEY]: mergedEditorStateMapJson,
        }
      : rootIdFromNode;
  }
  if (nextValue !== undefined) {
    nextValue = attachPreservedWorkstreamTableMetadata(nextValue, cur);
  }
  return nextValue;
}

const visitLexicalNodeTree = (node: LexicalNode, visit: (node: LexicalNode) => void) => {
  visit(node);
  if (!$isElementNode(node)) return;

  node.getChildren().forEach((child) => visitLexicalNodeTree(child, visit));
};

export function persistWorkstreamTableEditorStateMapKeyAcrossDocument({
  editor,
  currentNodeKey,
  mapKey,
  serialized,
  sourceText,
  isNarrativeFlatMode,
}: {
  editor: LexicalEditor;
  currentNodeKey: string;
  mapKey: string;
  serialized: string | undefined;
  sourceText: string | undefined;
  isNarrativeFlatMode: boolean;
}): unknown | undefined {
  let currentNodeNextValue: unknown | undefined;

  editor.update(
    () => {
      visitLexicalNodeTree($getRoot(), (node) => {
        if (!$isDynamicNode(node) || node.getKindId() !== WORKSTREAM_TABLE_EMBED_KIND) {
          return;
        }

        const nextValue = mergePersistedWorkstreamTableEmbedNodeValue(
          {
            rawNodeValue: node.getValue(),
            mapKey,
            serializedState: serialized,
            sourceText,
          },
          isNarrativeFlatMode
        );
        if (nextValue === undefined) {
          return;
        }

        node.setValue(nextValue);
        if (node.getKey() === currentNodeKey) {
          currentNodeNextValue = nextValue;
        }
      });
    },
    {discrete: true}
  );

  return currentNodeNextValue;
}

type WorkstreamTableEditorStateMapUpdate = {
  sourceNodeKey: string;
  mapKey: string;
  serialized: string | undefined;
  sourceText: string | undefined;
};

const workstreamTableEditorStateMapUpdateListeners = new Set<
  (update: WorkstreamTableEditorStateMapUpdate) => void
>();

const subscribeWorkstreamTableEditorStateMapUpdates = (
  listener: (update: WorkstreamTableEditorStateMapUpdate) => void
) => {
  workstreamTableEditorStateMapUpdateListeners.add(listener);
  return () => {
    workstreamTableEditorStateMapUpdateListeners.delete(listener);
  };
};

const notifyWorkstreamTableEditorStateMapUpdate = (update: WorkstreamTableEditorStateMapUpdate) => {
  workstreamTableEditorStateMapUpdateListeners.forEach((listener) => listener(update));
};

const WorkstreamTableEmbed = ({nodeKey, editor, value, setValue}: DynamicNodeRenderProps) => {
  const planningId = usePlanningId();
  const collabId = useCollabId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const embedMetricMutations = useWorkstreamMetricMutation();
  const {planning} = usePlanning(planningId ?? '');
  const {narrativeWorkstreams, isLoading: narrativeWorkstreamsLoading} = useEditorWorkstreams(
    planningId ?? ''
  );

  const switchboardId = planning?.switchboard_id ?? undefined;
  const {workstream: switchboardWorkstream, isLoading: switchboardWorkstreamLoading} =
    useWorkstream(switchboardId);

  const accessibleNarrative = useMemo(
    () => narrativeWorkstreams.filter((w) => w.is_accessible),
    [narrativeWorkstreams]
  );

  const isNarrativeFlatMode = accessibleNarrative.length >= 1;
  const narrativeFlatIds = useMemo(
    () => (isNarrativeFlatMode ? accessibleNarrative.map((w) => w.workstream_id) : []),
    [accessibleNarrative, isNarrativeFlatMode]
  );

  const narrativeDetailResults = useQueries({
    queries: narrativeFlatIds.map((workstreamId) => ({
      queryKey: [...workstreamDataKeys.all, 'workstream-detail', workstreamId],
      queryFn: () => getService(WorkstreamService).getApiV1WorkstreamIdGet(workstreamId),
      enabled: isNarrativeFlatMode && narrativeFlatIds.length > 0,
      staleTime: 60 * 1000,
    })),
  });

  const tableContext = getTypeaheadNodeTableContext(editor, nodeKey);
  const {bodyRowCount, rowIndex, workstreamNameNodeCount} = tableContext;

  const switchboardSubWorkstreamIds = useMemo(
    () => switchboardWorkstream?.dependencies.map((dependency) => dependency.dependent.id) ?? [],
    [switchboardWorkstream?.dependencies]
  );

  const tableContextSlice = useMemo(
    () => ({bodyRowCount, rowIndex, workstreamNameNodeCount}),
    [bodyRowCount, rowIndex, workstreamNameNodeCount]
  );

  const resolvedFromPlanning = useMemo(() => {
    if (accessibleNarrative.length >= 1) {
      return undefined;
    }
    if (switchboardId && switchboardWorkstream) {
      return resolveSwitchboardTableEmbedRootId(
        switchboardId,
        switchboardSubWorkstreamIds,
        tableContextSlice
      );
    }
    return undefined;
  }, [
    accessibleNarrative,
    switchboardId,
    switchboardSubWorkstreamIds,
    switchboardWorkstream,
    tableContextSlice,
  ]);

  const persistedId = getRootWorkstreamIdFromValue(value);
  const rootId = isNarrativeFlatMode ? undefined : (resolvedFromPlanning ?? persistedId);
  const persistedTableCommentOriginId = getWorkstreamTableCommentOriginIdFromValue(value);
  const [tableCommentOriginId, setTableCommentOriginId] = useState(
    () => persistedTableCommentOriginId ?? createWorkstreamTableCommentOriginId()
  );
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (persistedTableCommentOriginId) {
      if (persistedTableCommentOriginId !== tableCommentOriginId) {
        setTableCommentOriginId(persistedTableCommentOriginId);
      }
      return;
    }

    const nextValue = valueWithWorkstreamTableCommentOriginId({
      value,
      rootId,
      originId: tableCommentOriginId,
    });
    valueRef.current = nextValue;
    setValue(nextValue);
  }, [persistedTableCommentOriginId, rootId, setValue, tableCommentOriginId, value]);

  const {
    columns: embedTableColumns,
    tableWidthPx: embedTableWidthPx,
    onColumnResizeEnd,
  } = useColumnResizePersistence({
    editor,
    nodeKey,
    setValue,
    baseColumns: EMBED_TABLE_COLUMNS,
    value,
    enabled: ENABLE_SYNC_COLUMNS,
  });

  const persistEditorStateMapKey = useCallback<PersistEditorStateMapKey>(
    (mapKey, serialized, sourceText) => {
      const currentNodeNextValue = persistWorkstreamTableEditorStateMapKeyAcrossDocument({
        editor,
        currentNodeKey: nodeKey,
        mapKey,
        serialized,
        sourceText,
        isNarrativeFlatMode,
      });

      if (currentNodeNextValue !== undefined) {
        valueRef.current = currentNodeNextValue;
        setValue(currentNodeNextValue);
      }
      notifyWorkstreamTableEditorStateMapUpdate({
        sourceNodeKey: nodeKey,
        mapKey,
        serialized,
        sourceText,
      });
    },
    [editor, isNarrativeFlatMode, nodeKey, setValue]
  );

  useEffect(() => {
    return subscribeWorkstreamTableEditorStateMapUpdates((update) => {
      if (update.sourceNodeKey === nodeKey) {
        return;
      }

      const nextValue = mergePersistedWorkstreamTableEmbedNodeValue(
        {
          rawNodeValue: valueRef.current,
          mapKey: update.mapKey,
          serializedState: update.serialized,
          sourceText: update.sourceText,
        },
        isNarrativeFlatMode
      );
      if (nextValue === undefined) {
        return;
      }

      valueRef.current = nextValue;
      setValue(nextValue);
    });
  }, [isNarrativeFlatMode, nodeKey, setValue]);

  const handleCollabTitleChange = useCallback(
    (entityId: number, contentEditorState: string | undefined, sourceText?: string) => {
      persistEditorStateMapKey(
        planningMapKeys.workstreamTable.title(entityId),
        contentEditorState,
        sourceText
      );
    },
    [persistEditorStateMapKey]
  );

  const handleCollabMetricOwnerChange = useCallback(
    (metricId: number, contentEditorState: string | undefined, sourceText?: string) => {
      persistEditorStateMapKey(
        planningMapKeys.embedOutcome.owner(metricId),
        contentEditorState,
        sourceText
      );
    },
    [persistEditorStateMapKey]
  );

  const handleCollabOutcomeEtaChange = useCallback(
    (mapKey: string, contentEditorState: string | undefined, sourceText?: string) => {
      persistEditorStateMapKey(mapKey, contentEditorState, sourceText);
    },
    [persistEditorStateMapKey]
  );

  const {mutateAsync: updatePlanningWorkstreams} = useWorkstreamsUpdate(planningId ?? '');
  const handleNarrativeRemoveFromPlanning = useCallback(
    async (workstreamId: number) => {
      if (!planningId) {
        return;
      }
      await updatePlanningWorkstreams({
        workstreams: [workstreamId],
        action: 'remove',
        workstreamType: EditorWorkstreamType.NARRATIVE,
      });
    },
    [planningId, updatePlanningWorkstreams]
  );

  const planningCollabOutcomeComments = useMemo(():
    | PlanningCollabOutcomeCommentsConfig
    | undefined => {
    if (!collabId) {
      return undefined;
    }
    return {
      documentId: collabId,
      initialContentEditorState: getEditorStateMapJsonFromValue(value) ?? '{}',
      // Metric/outcome titles use the same `editorStateMap` keys as workstream name rows
      // (`planningMapKeys.embedOutcome.title` === `String(metricId)`); keep this wired to the
      // synced table-map persister so duplicate table embeds show the same thread anchors.
      handleContentEditorStateChange: handleCollabTitleChange,
      handleMetricOwnerContentEditorStateChange: handleCollabMetricOwnerChange,
      handleOutcomeEtaEditorStateChange: handleCollabOutcomeEtaChange,
    };
  }, [
    collabId,
    handleCollabMetricOwnerChange,
    handleCollabOutcomeEtaChange,
    handleCollabTitleChange,
    value,
  ]);

  useEffect(() => {
    if (isNarrativeFlatMode) {
      return;
    }
    if (!resolvedFromPlanning) {
      return;
    }
    const currentRoot = getRootWorkstreamIdFromValue(value);
    if (resolvedFromPlanning === currentRoot) {
      return;
    }
    const editorStateMapJson = getEditorStateMapJsonFromValue(value);
    const hasStoredEditorStateMap = editorStateMapJson !== undefined && editorStateMapJson !== '{}';
    let next: unknown = hasStoredEditorStateMap
      ? {v: resolvedFromPlanning, [WORKSTREAM_TABLE_EDITOR_STATE_MAP_KEY]: editorStateMapJson}
      : resolvedFromPlanning;
    next = attachPreservedWorkstreamTableMetadata(next, value);
    valueRef.current = next;
    setValue(next);
  }, [isNarrativeFlatMode, resolvedFromPlanning, setValue, value]);

  const [expanded, setExpanded] = useState(true);
  const [redesignOutcomeFilters, setRedesignOutcomeFilters] = useState<RedesignOutcomeFilter[]>(
    () => [...REDESIGN_OUTCOME_FILTERS]
  );
  const [selectedMetricsPeriod, setSelectedMetricsPeriod] = useState(() =>
    planningId ? getPlanningTrackerDefaultMetricsPeriod() : getCurrentMetricsPeriod()
  );
  const [selectedMetricsForecastType, setSelectedMetricsForecastType] =
    useState<MetricsForecastType>(() =>
      planningId ? PLANNING_TRACKER_DEFAULT_METRICS_FORECAST_TYPE : 'target'
    );
  const {data: rootWorkstream, isLoading: detailLoading} = useWorkstreamDetail(
    isNarrativeFlatMode ? undefined : rootId
  );

  const sortedDependencies = useMemo(() => {
    const deps = rootWorkstream?.dependencies ?? [];
    return [...deps].sort(sortDependenciesByStatusAndOrder);
  }, [rootWorkstream?.dependencies]);

  // Dependency summaries omit the viewer-specific confidential-metric count. When expanded,
  // hydrate each displayed sub-workstream from the same detail endpoint used for the root row.
  const expandedDependencyIds = useMemo(
    () =>
      !isNarrativeFlatMode && expanded
        ? sortedDependencies.map((dependency) => dependency.dependent.id)
        : [],
    [expanded, isNarrativeFlatMode, sortedDependencies]
  );
  const expandedDependencyDetailResults = useQueries({
    queries: expandedDependencyIds.map((workstreamId) => ({
      queryKey: [...workstreamDataKeys.all, 'workstream-detail', workstreamId],
      queryFn: () => getService(WorkstreamService).getApiV1WorkstreamIdGet(workstreamId),
      enabled: expandedDependencyIds.length > 0,
      staleTime: 60 * 1000,
    })),
  });
  const workstreamTableData: EmbedWorkstreamRow[] = (() => {
    if (isNarrativeFlatMode) {
      const rows: EmbedWorkstreamRow[] = [];
      for (let i = 0; i < narrativeFlatIds.length; i++) {
        const data = narrativeDetailResults[i]?.data;
        if (data) {
          rows.push({workstream: data, isDependency: false});
        }
      }
      return rows;
    }
    if (!rootWorkstream) return [];
    const main = {workstream: rootWorkstream, isDependency: false as const};
    if (!expanded) return [main];
    const subs = sortedDependencies.map((dep, index) => ({
      workstream:
        expandedDependencyDetailResults[index]?.data ??
        (dep.dependent as WorkstreamBasicWithSummary),
      isDependency: true as const,
    }));
    return [main, ...subs];
  })();

  const toggleRedesignOutcomeFilter = useCallback((filter: RedesignOutcomeFilter) => {
    setRedesignOutcomeFilters((currentFilters) => {
      if (currentFilters.includes(filter)) {
        if (currentFilters.length === 1) {
          return currentFilters;
        }
        return REDESIGN_OUTCOME_FILTERS.filter(
          (candidate) => candidate !== filter && currentFilters.includes(candidate)
        );
      }

      return REDESIGN_OUTCOME_FILTERS.filter(
        (candidate) => candidate === filter || currentFilters.includes(candidate)
      );
    });
  }, []);

  const embedWorkstreamsForMetricPeriods = workstreamTableData.map((row) => row.workstream);

  const currentMetricsPeriod = getCurrentMetricsPeriod();

  const planningMetricsDefaultPeriod = useMemo(
    () => (planningId ? getPlanningTrackerDefaultMetricsPeriod() : undefined),
    [planningId]
  );

  const metricsPeriodOptions = useMemo(() => {
    const forecastPeriods = embedWorkstreamsForMetricPeriods.flatMap((workstream) =>
      getFullWorkstreamMetrics(workstream.metrics)
        .filter((metric) => metric.type === 'metric')
        .flatMap((metric) => metric.metric_forecasts?.map((forecast) => forecast.period) ?? [])
    );

    if (forecastPeriods.length === 0) {
      if (!planningMetricsDefaultPeriod) {
        return [currentMetricsPeriod];
      }
      const rangeStart =
        compareMetricsPeriods(planningMetricsDefaultPeriod, currentMetricsPeriod) <= 0
          ? planningMetricsDefaultPeriod
          : currentMetricsPeriod;
      const rangeEnd =
        compareMetricsPeriods(planningMetricsDefaultPeriod, currentMetricsPeriod) >= 0
          ? planningMetricsDefaultPeriod
          : currentMetricsPeriod;
      return normalizeMetricsPeriodOptions(buildMetricsPeriodRange(rangeStart, rangeEnd));
    }

    const sortedForecastPeriods = [...forecastPeriods].sort(compareMetricsPeriods);
    const earliestForecastPeriod = sortedForecastPeriods[0];
    const latestForecastPeriod = sortedForecastPeriods[sortedForecastPeriods.length - 1];
    const minimumLatestPeriod = getMetricsPeriodWithOffset(currentMetricsPeriod, 2);
    let endPeriod =
      compareMetricsPeriods(latestForecastPeriod, minimumLatestPeriod) >= 0
        ? latestForecastPeriod
        : minimumLatestPeriod;
    let startPeriod =
      compareMetricsPeriods(currentMetricsPeriod, earliestForecastPeriod) <= 0
        ? currentMetricsPeriod
        : earliestForecastPeriod;

    if (
      planningMetricsDefaultPeriod &&
      compareMetricsPeriods(planningMetricsDefaultPeriod, startPeriod) < 0
    ) {
      startPeriod = planningMetricsDefaultPeriod;
    }
    if (
      planningMetricsDefaultPeriod &&
      compareMetricsPeriods(planningMetricsDefaultPeriod, endPeriod) > 0
    ) {
      endPeriod = planningMetricsDefaultPeriod;
    }

    return normalizeMetricsPeriodOptions(buildMetricsPeriodRange(startPeriod, endPeriod));
  }, [currentMetricsPeriod, embedWorkstreamsForMetricPeriods, planningMetricsDefaultPeriod]);

  useEffect(() => {
    if (metricsPeriodOptions.includes(selectedMetricsPeriod)) {
      return;
    }

    const preferredPlanningPeriod =
      planningMetricsDefaultPeriod && metricsPeriodOptions.includes(planningMetricsDefaultPeriod)
        ? planningMetricsDefaultPeriod
        : undefined;

    setSelectedMetricsPeriod(
      preferredPlanningPeriod ??
        (metricsPeriodOptions.includes(currentMetricsPeriod)
          ? currentMetricsPeriod
          : (metricsPeriodOptions[0] ?? currentMetricsPeriod))
    );
  }, [
    currentMetricsPeriod,
    metricsPeriodOptions,
    planningMetricsDefaultPeriod,
    selectedMetricsPeriod,
  ]);

  const columnLabelOverrides = useMemo(() => {
    const overrides: Record<string, string> = {};
    for (const col of COLUMN_CONFIGS) {
      overrides[col.id] = col.label;
    }
    return overrides;
  }, []);

  const columnHeaderContent = useMemo((): Record<string, ReactNode> | undefined => {
    return {
      metrics: (
        <div className={embedStyles.metricsHeaderControl}>
          <RedesignMetricsPeriodSelector
            periods={metricsPeriodOptions}
            selectedPeriod={selectedMetricsPeriod}
            selectedForecastType={selectedMetricsForecastType}
            onSelect={setSelectedMetricsPeriod}
            onForecastTypeSelect={setSelectedMetricsForecastType}
          />
        </div>
      ),
      outcomes: (
        <div className={embedStyles.metricsHeaderControl}>
          <RedesignOutcomeTypeSelector
            selectedFilters={redesignOutcomeFilters}
            onToggle={toggleRedesignOutcomeFilter}
          />
        </div>
      ),
    };
  }, [
    metricsPeriodOptions,
    redesignOutcomeFilters,
    selectedMetricsForecastType,
    selectedMetricsPeriod,
    toggleRedesignOutcomeFilter,
  ]);

  const columnHeaderLabel = useMemo((): Record<string, ReactNode> | undefined => {
    if (!planningId) {
      return undefined;
    }
    const mapJson = getEditorStateMapJsonFromValue(value);
    const headerFallbackText = (colType: string) =>
      columnLabelOverrides[colType] ? t(columnLabelOverrides[colType]) : t(colType);

    return {
      workstream: (
        <PlanningCommentField
          mapKey={planningMapKeys.workstreamTable.columnHeader('workstream')}
          mapJson={mapJson}
          fallbackText={headerFallbackText('workstream')}
          planningId={planningId}
          onPersistKey={persistEditorStateMapKey}
          textBold
          fallback={(text) => (
            <Text isBold size="small">
              {text}
            </Text>
          )}
        />
      ),
      metrics: (
        <PlanningCommentField
          mapKey={planningMapKeys.workstreamTable.columnHeader('metrics')}
          mapJson={mapJson}
          fallbackText={headerFallbackText('metrics')}
          planningId={planningId}
          onPersistKey={persistEditorStateMapKey}
          textBold
          fallback={(text) => (
            <Text isBold size="small">
              {text}
            </Text>
          )}
        />
      ),
      outcomes: (
        <PlanningCommentField
          mapKey={planningMapKeys.workstreamTable.columnHeader('outcomes')}
          mapJson={mapJson}
          fallbackText={headerFallbackText('outcomes')}
          planningId={planningId}
          onPersistKey={persistEditorStateMapKey}
          textBold
          fallback={(text) => (
            <Text isBold size="small">
              {text}
            </Text>
          )}
        />
      ),
    };
  }, [columnLabelOverrides, persistEditorStateMapKey, planningId, value]);

  const bodyItems: EmbedBodyItem[] = (() => {
    if (isNarrativeFlatMode) {
      return workstreamTableData.map((row) => ({kind: 'row' as const, ...row}));
    }
    if (!rootWorkstream) return [];
    if (!expanded || sortedDependencies.length === 0) {
      return [{kind: 'row', workstream: rootWorkstream, isDependency: false}];
    }
    return [
      {kind: 'row', workstream: rootWorkstream, isDependency: false},
      {
        kind: 'section_header',
        dependencyCount: sortedDependencies.length,
      },
      ...sortedDependencies.map((dep, index) => ({
        kind: 'row' as const,
        workstream:
          expandedDependencyDetailResults[index]?.data ??
          (dep.dependent as WorkstreamBasicWithSummary),
        isDependency: true as const,
      })),
    ];
  })();

  const showPlanningEmbedChrome = editor.isEditable() && !isPlanningDocPublished;

  const renderEmbedNameCell = (
    row: EmbedWorkstreamRow,
    expandedState: boolean,
    onToggleSubworkstreams?: () => void
  ) => {
    if (isNarrativeFlatMode) {
      if (showPlanningEmbedChrome) {
        return (
          <NarrativePlanningEmbedNameColumn
            editor={editor}
            workstream={row.workstream}
            isDependency={row.isDependency}
            isSubworkstreamsExpanded={expandedState}
            onToggleSubworkstreams={onToggleSubworkstreams}
            suppressSubworkstreamUi
            embedValue={value}
            planningId={planningId ?? undefined}
            onCollabTitleChange={handleCollabTitleChange}
            persistMapKey={persistEditorStateMapKey}
            onRemoveFromPlanning={handleNarrativeRemoveFromPlanning}
          />
        );
      }
      return (
        <EmbedNameColumn
          workstream={row.workstream}
          isDependency={row.isDependency}
          isSubworkstreamsExpanded={expandedState}
          onToggleSubworkstreams={onToggleSubworkstreams}
          suppressSubworkstreamUi
        />
      );
    }
    if (!rootWorkstream) {
      return (
        <EmbedNameColumn
          workstream={row.workstream}
          isDependency={row.isDependency}
          isSubworkstreamsExpanded={expandedState}
          onToggleSubworkstreams={onToggleSubworkstreams}
        />
      );
    }
    if (showPlanningEmbedChrome) {
      return (
        <SwitchboardPlanningEmbedNameColumn
          editor={editor}
          workstream={row.workstream}
          isDependency={row.isDependency}
          parentSwitchboardId={rootWorkstream.id}
          isSubworkstreamsExpanded={expandedState}
          onToggleSubworkstreams={onToggleSubworkstreams}
          embedValue={value}
          planningId={planningId ?? undefined}
          persistEditorStateMapKey={persistEditorStateMapKey}
        />
      );
    }
    return (
      <EmbedNameColumn
        workstream={row.workstream}
        isDependency={row.isDependency}
        isSubworkstreamsExpanded={expandedState}
        onToggleSubworkstreams={onToggleSubworkstreams}
      />
    );
  };

  const showSwitchboardAddWorkstreamRow =
    !isNarrativeFlatMode && !!rootWorkstream && showPlanningEmbedChrome;

  const flatNarrativeDetailLoading =
    isNarrativeFlatMode && narrativeDetailResults.some((q) => q.isPending || q.isLoading);

  if (isNarrativeFlatMode) {
    if (!planningId) {
      return (
        <Box paddingY="4">
          <Text size="small" color="subtle">
            {t('dynamic_node_workstream_table_no_planning')}
          </Text>
        </Box>
      );
    }
    if (narrativeWorkstreamsLoading) {
      return <WorkstreamTableEmbedTableLoading editor={editor} />;
    }
    if (flatNarrativeDetailLoading) {
      return <WorkstreamTableEmbedTableLoading editor={editor} />;
    }
    if (
      narrativeDetailResults.length > 0 &&
      narrativeDetailResults.every((q) => !q.isPending && !q.isLoading) &&
      workstreamTableData.length === 0
    ) {
      return (
        <Box paddingY="4">
          <Text size="small" color="subtle">
            {t('dynamic_node_workstream_table_no_narrative')}
          </Text>
        </Box>
      );
    }
    if (workstreamTableData.length > 0) {
      return (
        <PlanningTableWideShell
          editor={editor}
          fillsContainer
          tableWidthPx={embedTableWidthPx}
          commentOriginId={tableCommentOriginId}
        >
          <Table
            columns={embedTableColumns}
            data={workstreamTableData}
            headerTextSize="small"
            bodyCellPadding={8}
            columnLabelOverrides={columnLabelOverrides}
            columnHeaderLabel={columnHeaderLabel}
            columnHeaderContent={columnHeaderContent}
            lastColumnResizeHandle
            onColumnResizeEnd={onColumnResizeEnd}
            style={PLANNING_TABLE_SHELL_STYLE}
          >
            <Table.Header />
            <Table.Body>
              {workstreamTableData.map((row) => (
                <Table.Row key={row.workstream.id}>
                  <Table.Cell>{renderEmbedNameCell(row, false, undefined)}</Table.Cell>
                  <Table.Cell>
                    <EmbedMetricsColumn
                      workstream={row.workstream}
                      planningCollabOutcomeComments={planningCollabOutcomeComments}
                      isPlanningDocPublished={isPlanningDocPublished}
                      metricMutations={embedMetricMutations}
                      metricPeriodOptions={metricsPeriodOptions}
                      selectedMetricsPeriod={selectedMetricsPeriod}
                      selectedMetricsForecastType={selectedMetricsForecastType}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <EmbedOutcomesColumn
                      workstream={row.workstream}
                      planningCollabOutcomeComments={planningCollabOutcomeComments}
                      isPlanningDocPublished={isPlanningDocPublished}
                      metricMutations={embedMetricMutations}
                      selectedFilters={redesignOutcomeFilters}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </PlanningTableWideShell>
      );
    }
    return (
      <Box paddingY="4">
        <Text size="small" color="subtle">
          {t('dynamic_node_workstream_table_no_narrative')}
        </Text>
      </Box>
    );
  }

  if (!rootId) {
    if (!planningId) {
      return (
        <Box paddingY="4">
          <Text size="small" color="subtle">
            {t('dynamic_node_workstream_table_no_planning')}
          </Text>
        </Box>
      );
    }
    if (
      (switchboardId && switchboardWorkstreamLoading) ||
      (!switchboardId && narrativeWorkstreamsLoading)
    ) {
      return <WorkstreamTableEmbedTableLoading editor={editor} />;
    }
    return (
      <Box paddingY="4">
        <Text size="small" color="subtle">
          {t('dynamic_node_workstream_table_no_narrative')}
        </Text>
      </Box>
    );
  }

  if (detailLoading || !rootWorkstream) {
    return <WorkstreamTableEmbedTableLoading editor={editor} />;
  }

  return (
    <PlanningTableWideShell
      editor={editor}
      fillsContainer
      tableWidthPx={embedTableWidthPx}
      commentOriginId={tableCommentOriginId}
    >
      <Table
        columns={embedTableColumns}
        data={workstreamTableData}
        headerTextSize="small"
        bodyCellPadding={8}
        columnLabelOverrides={columnLabelOverrides}
        columnHeaderLabel={columnHeaderLabel}
        columnHeaderContent={columnHeaderContent}
        lastColumnResizeHandle
        onColumnResizeEnd={onColumnResizeEnd}
        style={PLANNING_TABLE_SHELL_STYLE}
      >
        <Table.Header />
        <Table.Body>
          {bodyItems.map((item) =>
            item.kind === 'section_header' ? (
              <SubworkstreamSectionHeaderRow
                key={`${rootWorkstream.id}-subworkstream-section`}
                colSpan={embedTableColumns.length}
                dependencyCount={item.dependencyCount}
                onCollapse={() => setExpanded(false)}
              />
            ) : (
              <Table.Row
                key={item.workstream.id}
                className={item.isDependency ? embedStyles.subworkstreamRow : undefined}
                data-subworkstream={item.isDependency ? 'true' : undefined}
              >
                <Table.Cell>
                  {renderEmbedNameCell(
                    {
                      workstream: item.workstream,
                      isDependency: item.isDependency,
                    },
                    expanded,
                    !item.isDependency && getSubworkstreamCount(item.workstream) > 0
                      ? () => setExpanded((e) => !e)
                      : undefined
                  )}
                </Table.Cell>
                <Table.Cell>
                  <EmbedMetricsColumn
                    workstream={item.workstream}
                    planningCollabOutcomeComments={planningCollabOutcomeComments}
                    isPlanningDocPublished={isPlanningDocPublished}
                    metricMutations={embedMetricMutations}
                    metricPeriodOptions={metricsPeriodOptions}
                    selectedMetricsPeriod={selectedMetricsPeriod}
                    selectedMetricsForecastType={selectedMetricsForecastType}
                  />
                </Table.Cell>
                <Table.Cell>
                  <EmbedOutcomesColumn
                    workstream={item.workstream}
                    planningCollabOutcomeComments={planningCollabOutcomeComments}
                    isPlanningDocPublished={isPlanningDocPublished}
                    metricMutations={embedMetricMutations}
                    selectedFilters={redesignOutcomeFilters}
                  />
                </Table.Cell>
              </Table.Row>
            )
          )}
          {showSwitchboardAddWorkstreamRow ? (
            <SwitchboardEmbedAddWorkstreamRow
              editor={editor}
              parentSwitchboardId={rootWorkstream.id}
              colSpan={embedTableColumns.length}
            />
          ) : null}
        </Table.Body>
      </Table>
    </PlanningTableWideShell>
  );
};

export const workstreamTableEmbedDefinition: DynamicNodeDefinition = {
  kindId: WORKSTREAM_TABLE_EMBED_KIND,
  labelKey: 'dynamic_node_workstream_table',
  categoryKey: 'dynamic_node_category_workstreams',
  defaultBinding: {kind: WORKSTREAM_TABLE_EMBED_KIND} satisfies WorkstreamTableEmbedBinding,
  Component: WorkstreamTableEmbed,
  exportHtml: ({value}) => {
    const id = getRootWorkstreamIdFromValue(value);
    return id ? markdownBracketReference(t('dynamic_node_workstream_table'), String(id)) : '';
  },
};
